// Release notes (wave 3, plain document look): the internal call-centre
// release document as cards on the portal's theme (tokens, no hex, no
// inline styles). The words are the document's own and are kept as they
// were (an English internal document); nothing is read or written.
import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SosOutlinedIcon from '@mui/icons-material/SosOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { tokens as c } from '../theme';
import logoImage from '../assets/img/logowebsite.png';
import { InlineMessage } from '../components/feedback';
import { DetailCard, IconCircle, InfoRow, SectionCard, StatusChip } from '../components/modern';

// A bulleted list in the theme's text style.
function Bullets({ children, sx }) {
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 3,
        color: c.INK,
        fontSize: 14.5,
        lineHeight: 1.7,
        '& li': { mb: 0.5 },
        '& strong': { fontWeight: 700 },
        ...(sx || {}),
      }}
    >
      {children}
    </Box>
  );
}

// A sub-heading inside a card.
function SubTitle({ icon, hue, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
      {icon ? <IconCircle icon={icon} hue={hue} size={28} /> : null}
      <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: c.INK }}>
        {children}
      </Typography>
    </Box>
  );
}

const ReleaseNotes = () => {
  const { t, i18n } = useTranslation();
  const body = { fontSize: 15, lineHeight: 1.8, color: c.INK };

  return (
    <Box dir={i18n.dir()} sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5, pb: 2 }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2.5, md: 3.5 }, display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
        <Box
          component="img"
          src={logoImage}
          alt="Logo"
          sx={{ width: 88, height: 88, borderRadius: '50%', bgcolor: c.WHITE, border: `1px solid ${c.OUTLINE}`, objectFit: 'contain', flex: 'none' }}
        />
        <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
          <Typography component="p" sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.04em', color: c.ACCENT_TEXT }}>
            {t('AppName', { defaultValue: 'DONKEY DRIVE' })}
          </Typography>
          <Typography component="h2" sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, lineHeight: 1.25, color: c.INK }}>
            Release Notes &amp; Documentation
          </Typography>
          <Typography sx={{ fontSize: 15, color: c.INK_2, mt: 0.5 }}>Release Document v2.0.14 | Internal Call Center Documentation</Typography>
          <StatusChip tone="accent" icon={<LockOutlinedIcon />} label="INTERNAL USE ONLY - CALL CENTER TEAM" sx={{ mt: 1.5 }} />
        </Box>
      </Paper>

      {/* Release Info */}
      <DetailCard icon={<NewReleasesOutlinedIcon />} hue="info" title="Release Version: v2.0.14">
        <InfoRow label="Release Version:">v2.0.14</InfoRow>
        <InfoRow label="Feature Name:">Operations Enhancement Suite</InfoRow>
        <InfoRow label="Release Date:">February 9, 2026</InfoRow>
        <InfoRow label="Target Users:">Call Center Agents</InfoRow>
      </DetailCard>

      {/* Summary */}
      <SectionCard icon={<ArticleOutlinedIcon />} hue="accent" title="Version 2.0.14 Summary">
        <Typography sx={{ ...body, mt: 1 }}>
          This release introduces enhanced transparency features for booking details, giving call center agents and supervisors better visibility into
          distance calculations and pricing decisions. Key additions include a new Distance Tracking card that shows how trip distances were calculated
          (GPS, estimate, or Google Maps), GPS tracking quality indicators, and a Price Override detection system that clearly shows when customers have
          manually adjusted fares. These features help agents quickly identify potential pricing issues and provide accurate information to customers.
        </Typography>
      </SectionCard>

      {/* Key Features */}
      <SectionCard icon={<StarsOutlinedIcon />} hue="amber" title="Key Features - Version 2.0.14">
        <SubTitle>1. Price Override Detection (Booking Details)</SubTitle>
        <Bullets>
          <li>
            <strong>Offer Fare Tracking:</strong> Clearly shows when customer manually adjusted the fare
          </li>
          <li>
            <strong>Visual Indicators:</strong> Green arrow (↑) for higher offers, Red arrow (↓) for lower offers
          </li>
          <li>
            <strong>Percentage Display:</strong> Shows exact percentage difference from estimate
          </li>
          <li>
            <strong>Tooltip Details:</strong> Hover to see original estimate vs offered fare
          </li>
          <li>
            <strong>No Override Label:</strong> Shows &quot;No (Using Estimate)&quot; when standard pricing used
          </li>
        </Bullets>

        <SubTitle>2. Distance Tracking Transparency Card (Booking Details)</SubTitle>
        <Bullets>
          <li>
            <strong>Calculation Method:</strong> Shows how distance was calculated (GPS Tracked, Estimate Used, Google Maps)
          </li>
          <li>
            <strong>Color-Coded Badges:</strong> Green for GPS, Yellow for Estimate, Blue for Google Maps
          </li>
          <li>
            <strong>GPS Tracked Distance:</strong> Actual distance recorded from GPS coordinates
          </li>
          <li>
            <strong>Tracking Points Count:</strong> Number of GPS coordinates recorded during trip
          </li>
          <li>
            <strong>Quality Indicators:</strong> Excellent (≥50 points), Good (≥20), Fair (≥10), Poor (&lt;10)
          </li>
          <li>
            <strong>Distance Comparison:</strong> Side-by-side view of Estimate, GPS, and Final distances
          </li>
          <li>
            <strong>Variance Alerts:</strong> Color-coded warnings when GPS differs significantly from estimate
          </li>
        </Bullets>

        <SubTitle>3. Enhanced Tooltips</SubTitle>
        <Bullets>
          <li>
            <strong>Distance Method Tooltip:</strong> Explains what each calculation method means
          </li>
          <li>
            <strong>GPS Distance Tooltip:</strong> Shows comparison between GPS, Final, and Estimate distances
          </li>
          <li>
            <strong>Tracking Points Tooltip:</strong> Explains correlation between points and accuracy
          </li>
          <li>
            <strong>Price Override Tooltip:</strong> Shows original estimate and customer&apos;s offered fare
          </li>
        </Bullets>
      </SectionCard>

      {/* Visual Indicators */}
      <SectionCard icon={<PaletteOutlinedIcon />} hue="success" title="Visual Status Indicators">
        <Box sx={{ display: 'grid', gap: 2, mt: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
          <Box sx={{ p: 2, borderRadius: '12px', border: `1px solid ${c.OUTLINE}` }}>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, mb: 1 }}>
              Status Chips
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <StatusChip tone="success" icon={<CheckRoundedIcon />} label="Processed" />
              <StatusChip tone="warning" icon={<ScheduleRoundedIcon />} label="Pending" />
            </Box>
            <Typography sx={{ fontSize: 14, color: c.INK_2 }}>Used in SOS Reports and Complaints to show processing status</Typography>
          </Box>
          <Box sx={{ p: 2, borderRadius: '12px', border: `1px solid ${c.OUTLINE}` }}>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, mb: 1 }}>
              Row Styling
            </Typography>
            <Bullets>
              <li>Bold text for unprocessed items</li>
              <li>Light green background for processed</li>
              <li>Action icons change based on status</li>
            </Bullets>
          </Box>
        </Box>
      </SectionCard>

      {/* Implementation Details */}
      <SectionCard icon={<BuildOutlinedIcon />} hue="info" title="Implementation Details">
        <Box sx={{ display: 'grid', columnGap: 3, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
          <Box>
            <SubTitle icon={<PaymentsOutlinedIcon />} hue="success">
              Withdrawals Page
            </SubTitle>
            <Bullets>
              <li>Dialog for reference code entry</li>
              <li>SMS notification via cloud function</li>
              <li>Process date tracking</li>
              <li>Driver mobile number fetching</li>
            </Bullets>
          </Box>
          <Box>
            <SubTitle icon={<SosOutlinedIcon />} hue="danger">
              SOS Reports
            </SubTitle>
            <Bullets>
              <li>Processing dialog with details</li>
              <li>Mandatory comment field</li>
              <li>ProcessedBy tracking</li>
              <li>Read-only after processing</li>
            </Bullets>
          </Box>
          <Box>
            <SubTitle icon={<FeedbackOutlinedIcon />} hue="amber">
              Complaints
            </SubTitle>
            <Bullets>
              <li>Full complaint details view</li>
              <li>Resolution notes requirement</li>
              <li>Status chip indicators</li>
              <li>Audit trail display</li>
            </Bullets>
          </Box>
          <Box>
            <SubTitle icon={<DashboardOutlinedIcon />} hue="accent">
              Dashboard
            </SubTitle>
            <Bullets>
              <li>Complaint metrics card (Orange)</li>
              <li>SOS metrics card (Pink)</li>
              <li>Real-time count updates</li>
              <li>Total/Processed/Pending breakdown</li>
            </Bullets>
          </Box>
        </Box>
      </SectionCard>

      {/* Processing Guidelines */}
      <SectionCard icon={<ChecklistRoundedIcon />} hue="amber" title="Processing Guidelines">
        <InlineMessage kind="warning" title="Required Actions:" sx={{ mt: 1.5 }}>
          <Bullets sx={{ color: 'inherit', mt: 0.5 }}>
            <li>
              <strong>Withdrawals:</strong> Always enter reference code before processing
            </li>
            <li>
              <strong>SOS Reports:</strong> Document resolution actions in comments field
            </li>
            <li>
              <strong>Complaints:</strong> Add detailed resolution notes before marking as processed
            </li>
            <li>
              <strong>Audit Trail:</strong> All actions are tracked with timestamp and user ID
            </li>
            <li>
              <strong>SMS Notifications:</strong> Automatically sent for withdrawals with reference code
            </li>
          </Bullets>
        </InlineMessage>
      </SectionCard>

      {/* Important Notes */}
      <SectionCard icon={<PriorityHighRoundedIcon />} hue="danger" title="Important Notes">
        <Bullets sx={{ mt: 1, lineHeight: 2 }}>
          <li>All processing actions require mandatory documentation</li>
          <li>Processed items become read-only to maintain data integrity</li>
          <li>SMS notifications are sent automatically via cloud functions</li>
          <li>Dashboard metrics update in real-time</li>
          <li>Audit trails are permanent and cannot be modified</li>
          <li>Reference codes are required for withdrawal tracking</li>
        </Bullets>
      </SectionCard>

      {/* Archive Section */}
      <SectionCard icon={<Inventory2OutlinedIcon />} hue="neutral" title="Archive - Previous Releases">
        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, mt: 1.5 }}>
          Version 1.0.42 - Operations Enhancement Suite
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: c.TEXT_3, mb: 1 }}>Released: October 2025</Typography>
        <Bullets sx={{ color: c.INK_2, fontSize: 14 }}>
          <li>Withdrawal reference tracking with SMS notifications</li>
          <li>SOS Report Management System with mandatory documentation</li>
          <li>Complaint Processing Enhancement with audit trails</li>
          <li>Dashboard Metrics Integration (real-time counts)</li>
          <li>Driver Communication Tracking System</li>
        </Bullets>

        <Divider sx={{ my: 2 }} />

        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: c.INK }}>
          Version 1.0.41 - Trip Cost Monitoring
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: c.TEXT_3, mb: 1 }}>Released: October 2025</Typography>
        <Bullets sx={{ color: c.INK_2, fontSize: 14 }}>
          <li>Triple-Factor Analysis System (Price, Time, Distance)</li>
          <li>Intelligent Suspicion Score Algorithm (0-100%)</li>
          <li>Color-coded badges for trip cost variance</li>
          <li>Multi-factor tooltips and warning indicators</li>
          <li>Smart validation for different pickup/drop locations</li>
          <li>Confidence scoring with fraud detection</li>
        </Bullets>

        <Divider sx={{ my: 2 }} />

        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: c.INK }}>
          Version 1.0.40 and Earlier
        </Typography>
        <Typography sx={{ fontSize: 14, color: c.INK_2, mt: 0.5 }}>For documentation on earlier versions, please contact the development team.</Typography>
      </SectionCard>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', py: 2, color: c.TEXT_3, borderTop: `1px solid ${c.OUTLINE}` }}>
        <Typography sx={{ fontWeight: 700, color: c.INK_2 }}>{t('CompanyName', { defaultValue: 'Donkey Drive' })} Internal Documentation</Typography>
        <Typography sx={{ fontSize: 14 }}>Version 2.0.14 | February 9, 2026</Typography>
        <Typography sx={{ fontSize: 14 }}>Property of {t('CompanyName', { defaultValue: 'Donkey Drive' })} - Confidential</Typography>
      </Box>
    </Box>
  );
};

export default ReleaseNotes;
